import { Trans } from '@lingui/macro';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useCommunitySummaries } from 'atoms/community-summaries';
import PostLoginNavBar from 'components/post-login-nav-bar/PostLoginNavBar';
import SharedLayout from 'components/shared-layout/SharedLayout';
import React, { ReactNode } from 'react';
import { CommunityWithAggregates } from '@cocoplatform/coco-rtc-shared';
import defaultCoverUrl from '../../assets/images/penguin-group.png';
import DashboardLayout from 'components/dashboard-layout/DashboardLayout';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CircleIcon from '@mui/icons-material/Circle';
import { CommunityIcon } from 'components/min-sidebar-community-list/MinSidebarCommunityList';
import { TabPanelProps } from '@mui/lab';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function CommunityExplorerPage() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  return (
    <DashboardLayout hideOverviewPanel>
      <Stack>
        <Stack>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label='basic tabs example'
            >
              <Tab label='Explore Public Communities' {...a11yProps(0)} />
              <Tab label='Followed Communities' {...a11yProps(1)} />
              {/* <Tab label='Item Three' {...a11yProps(2)} /> */}
            </Tabs>
          </Box>
        </Stack>
        <Stack>
          <Box width='100%' p={8} pt={2}>
            <CustomTabPanel value={value} index={0}>
              <Typography textAlign='left' variant='h2'>
                <Trans>Featured Communities</Trans>
              </Typography>
              <Box mt={4}>
                <CommunityExplorerGrid />
              </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <Typography textAlign='left' variant='h2'>
                <Trans>Followed Communities</Trans>
              </Typography>
              <Box mt={4}>
                <CommunityExplorerGrid />
              </Box>
            </CustomTabPanel>
          </Box>
        </Stack>
      </Stack>
    </DashboardLayout>
  );
}

export function CommunityStat({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <Stack alignItems='center' direction='row'>
      {icon}
      <Typography color='var(--color-subtext)' ml={0.5} variant='body2'>
        {label}
      </Typography>
    </Stack>
  );
}

export function CommunityCard({
  community,
}: {
  community: CommunityWithAggregates;
}) {
  return (
    <Card
      sx={{
        border: '1px solid var(--color-border)',
        borderRadius: 2,
        transitionDuration: '0.1s',
        transitionTimingFunction: 'ease-in-out',
        ':hover': {
          boxShadow: 4,
          cursor: 'pointer',
          img: {
            opacity: 1,
          },
        },
      }}
    >
      <CardMedia
        component='img'
        alt='green iguana'
        height='120'
        image={community.bannerUrl ?? defaultCoverUrl}
        sx={{
          opacity: 0.9,
        }}
      />
      <CardContent
        sx={{
          position: 'relative',
        }}
      >
        <CommunityIcon
          style={{
            position: 'absolute',
            top: -30,
            left: 20,
          }}
          id={community.id + Math.random().toString()}
          name={community.name}
          isActive={false}
        />
        <Typography gutterBottom variant='h5' component='div'>
          {community.name}
        </Typography>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            lineHeight: '1.4em',
            maxHeight: `${1.4 * 2}em`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {community.description}
        </Typography>
        <Box mt={2} />
        <Stack spacing={1} direction='row' alignItems='center'>
          <CommunityStat
            icon={
              <FavoriteIcon
                sx={{ fontSize: '1rem', color: 'var(--color-subtext)' }}
              />
            }
            label={`${community.spaceCount} spaces`}
          />
          <CommunityStat
            icon={<CircleIcon sx={{ fontSize: '0.5rem', color: 'green' }} />}
            label={`${community.coCreatorCount} members`}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export function CommunityExplorerGrid() {
  const { communities } = useCommunitySummaries({
    public: true,
  });

  // repeat community 10 times for testing
  const communitiesWithDuplicates = [
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
    ...communities,
  ];
  return (
    <Grid container spacing={3} alignItems='stretch'>
      {communitiesWithDuplicates.map((community) => (
        <Grid xs={4} item key={community.id}>
          <CommunityCard community={community} />
        </Grid>
      ))}
    </Grid>
  );
}
